/**
 * Раздел «Аудит».
 *
 * Admin видит весь журнал, Senior Manager — только события своих аккаунтов.
 * Разделение делает сервер: клиент отправляет один и тот же запрос, а выдача
 * отличается. Никакой клиентской фильтрации здесь нет и быть не должно.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { AuditEventDto } from '@tw/shared-types';
import { ApiError } from '../lib/api.js';
import { useSession } from '../state/session.tsx';
import { cn } from '@/lib/utils';
import { useIsMobile } from '../lib/useMedia.ts';

const OUTCOME_DOT: Record<string, string> = {
  SUCCESS: 'text-dot-done',
  DENIED: 'text-dot-lost',
  ERROR: 'text-dot-progress',
};

const ACTION_LABELS: Record<string, string> = {
  // Апартаменты (docs/STAYS.md).
  STAY_ACCOUNT_CREATED: 'Апартаменты: аккаунт подключён',
  STAY_ACCOUNT_UPDATED: 'Апартаменты: аккаунт изменён',
  STAY_ACCOUNT_DELETED: 'Апартаменты: аккаунт удалён',
  STAY_ACCOUNT_ACCESS_GRANTED: 'Апартаменты: выдан доступ к аккаунту',
  STAY_ACCOUNT_ACCESS_REVOKED: 'Апартаменты: отозван доступ к аккаунту',
  STAY_ACCOUNT_REVEALED: 'Апартаменты: автовход в аккаунт',
  STAY_BOOKING_CREATED: 'Апартаменты: бронь записана',
  STAY_BOOKING_UPDATED: 'Апартаменты: бронь изменена',
  STAY_BOOKING_DELETED: 'Апартаменты: бронь удалена',
  STAY_BOOKING_CAPTURED: 'Апартаменты: бронь записана со страницы',
  STAY_PROPERTY_STATUS_CHANGED: 'Апартаменты: статус квартиры изменён',
  LOGIN: 'Вход',
  LOGIN_FAILED: 'Неудачный вход',
  LOGOUT: 'Выход',
  USER_CREATED: 'Создан пользователь',
  USER_ROLE_CHANGED: 'Изменена роль',
  USER_DEACTIVATED: 'Деактивирован пользователь',
  PASSWORD_CHANGED: 'Смена пароля',
  TELEGRAM_ACCOUNT_CONNECTED: 'Аккаунт подключён',
  TELEGRAM_ACCOUNT_DISCONNECTED: 'Аккаунт отключён',
  MESSAGE_SENT: 'Отправлено сообщение',
  MESSAGE_EDITED: 'Изменено сообщение',
  MESSAGE_DELETED: 'Удалено сообщение',
  CHAT_METADATA_UPDATED: 'Изменены данные чата',
  CHAT_ASSIGNED: 'Назначен ответственный',
  ACCOUNT_ASSIGNED_TO_SENIOR: 'Аккаунт закреплён за старшим',
  ACCOUNT_ASSIGNED_TO_MANAGER: 'Выдан доступ менеджеру',
  ACCOUNT_ASSIGNED_TO_INTERN: 'Выдан доступ стажёру',
  ACCOUNT_UNASSIGNED: 'Отозван доступ',
  PERMISSION_DENIED: 'Отказ в доступе',
};

export function AuditView() {
  const mobile = useIsMobile();
  const { api } = useSession();

  const [events, setEvents] = useState<AuditEventDto[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  /*
   * Курсор для запроса — в ref, а не из замыкания.
   *
   * Колбэк намеренно не пересоздаётся на смену курсора, и прочитанный из
   * замыкания он навсегда оставался начальным null: «Показать ещё» раз за
   * разом дописывала первую страницу.
   */
  const cursorRef = useRef<string | null>(null);
  const [outcome, setOutcome] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (reset: boolean) => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.audit({
          limit: 50,
          ...(reset || !cursorRef.current ? {} : { cursor: cursorRef.current }),
          ...(outcome ? { outcome } : {}),
        });
        setEvents((prev) => (reset ? res.items : [...prev, ...res.items]));
        cursorRef.current = res.nextCursor;
        setCursor(res.nextCursor);
      } catch (err) {
        setError(
          err instanceof ApiError && err.isForbidden
            ? 'Журнал недоступен для вашей роли'
            : 'Не удалось загрузить журнал',
        );
      } finally {
        setLoading(false);
      }
    },
    [api, outcome],
  );

  useEffect(() => {
    void load(true);
  }, [load]);

  const actorOf = (event: AuditEventDto): string =>
    event.actor?.displayName ?? (event.deletedActor ? `${event.deletedActor.displayName} (удалён)` : 'система');

  return (
    <div className={cn('h-full overflow-y-auto', mobile ? 'px-3 pb-4 pt-3' : 'rounded-2xl border border-border bg-card/60 p-6 shadow-md backdrop-blur-sm')}>
      {/*
        Заголовок раздела. Раньше страница начиналась сразу с выпадающего
        списка и таблицы — без единой подписи о том, что это и за какой
        период. Журнал открывают при разборе инцидента, и первое, что нужно
        знать, — что именно тут записано.
      */}
      <header className="mb-4 flex flex-wrap items-end justify-between gap-3">
        {/* На телефоне название раздела — в шапке приложения. */}
        <div className={cn(mobile && 'hidden')}>
          <h1 className="text-[28px] font-semibold leading-[1.1] tracking-[-0.027em] text-heading">
            Журнал аудита
          </h1>
          <p className="mt-1 max-w-[52ch] text-sm text-muted-foreground">
            Значимые действия сотрудников и системы, включая отказы в доступе.
            Записи не изменяются и не удаляются.
          </p>
        </div>

        <label className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
          Показывать
          <select
            className="h-9 rounded-lg border border-border bg-card px-3 text-sm text-foreground"
            value={outcome}
            onChange={(e) => setOutcome(e.target.value)}
          >
            <option value="">Все события</option>
            <option value="SUCCESS">Успешные</option>
            {/* Отказы — самая полезная выборка для безопасности: серия 403 у
                одного человека означает либо нехватку доступа, либо перебор
                чужих идентификаторов. */}
            <option value="DENIED">Отказы в доступе</option>
            <option value="ERROR">Ошибки</option>
          </select>
        </label>
      </header>

      {error && (
        <p className="mb-2 text-sm text-destructive" role="alert">
          {error}
        </p>
      )}

      {/* Телефон: событие карточкой — таблица в пять колонок не помещается. */}
      {mobile ? (
        <div className="flex flex-col divide-y divide-border/60 overflow-hidden rounded-2xl border border-border bg-card">
          {events.map((event) => (
            <div
              key={event.id}
              className={cn(
                'px-3.5 py-2.5 text-[13px]',
                event.outcome === 'DENIED' && 'text-destructive',
                event.outcome === 'ERROR' && 'text-[var(--warn)]',
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium">{ACTION_LABELS[event.action] ?? event.action}</span>
                <span className="flex flex-none items-center gap-1.5 text-[11px]">
                  <span aria-hidden className={cn('status-dot', OUTCOME_DOT[event.outcome] ?? 'text-muted-foreground')} />
                  {event.outcome}
                </span>
              </div>
              <p className="mt-0.5 text-[12px] text-muted-foreground">
                {actorOf(event)} · {new Date(event.occurredAt).toLocaleString('ru-RU')}
                {(event.targetUser?.displayName ?? event.chatId ?? event.telegramAccountId) && (
                  <> · <span className="font-mono text-[11px]">{event.targetUser?.displayName ?? event.chatId ?? event.telegramAccountId}</span></>
                )}
              </p>
            </div>
          ))}
        </div>
      ) : (
      <table className="w-full overflow-hidden rounded-2xl border border-border bg-card text-sm [border-collapse:separate] [border-spacing:0]">
        <thead>
          {/* Шапка липкая: журнал длинный, и при прокрутке колонки «Результат»
              и «Объект» без названий неразличимы. */}
          <tr className="sticky top-0 z-10 bg-card [&>th]:border-b [&>th]:border-border [&>th]:bg-card [&>th]:px-2.5 [&>th]:py-2.5 [&>th]:text-left [&>th]:text-[11px] [&>th]:font-semibold [&>th]:uppercase [&>th]:tracking-[0.05em] [&>th]:text-muted-foreground">
            <th>Время</th>
            <th>Сотрудник</th>
            <th>Действие</th>
            <th>Результат</th>
            <th>Объект</th>
          </tr>
        </thead>
        <tbody>
          {events.map((event) => (
            <tr
              key={event.id}
              className={cn(
                // Чередование строк: журнал плотный и однообразный, глаз
                // теряет строку на середине таблицы.
                'transition-colors odd:bg-secondary/40 hover:bg-accent [&>td]:border-b [&>td]:border-border [&>td]:px-2.5 [&>td]:py-2',
                // Отказы подсвечиваются: серия таких строк у одного человека —
                // сигнал, и он не должен теряться среди обычных записей.
                event.outcome === 'DENIED' && 'text-destructive',
                event.outcome === 'ERROR' && 'text-[var(--warn)]',
              )}
            >
              <td>{new Date(event.occurredAt).toLocaleString('ru-RU')}</td>
              {/*
                Автор удалённого сотрудника.

                Записи удалённого человека теряют ссылку на карточку, но не имя:
                оно сохраняется внутри записи. Без этой подстановки журнал
                показывал бы «система» — то есть приписывал бы действия человека
                никому, а это хуже отсутствия записи.
              */}
              <td>
                {event.actor?.displayName ??
                  (event.deletedActor
                    ? `${event.deletedActor.displayName} (удалён)`
                    : 'система')}
              </td>
              <td>{ACTION_LABELS[event.action] ?? event.action}</td>
              <td>
                {/*
                  Точка перед кодом исхода. Сам код оставлен как есть: это
                  журнал, и подменять значение переводом здесь нельзя — по нему
                  фильтруют и его же цитируют в разборе инцидента.

                  Цвет несёт точка, а не текст: заливки статусов контраст как
                  текст не проходят, а строка отказа и без того подсвечена
                  целиком.
                */}
                <span className="flex items-center gap-2">
                  <span aria-hidden className={cn('status-dot', OUTCOME_DOT[event.outcome] ?? 'text-muted-foreground')} />
                  {event.outcome}
                </span>
              </td>
              <td className="px-2.5 py-2 font-mono text-[11px] text-muted-foreground">
                {event.targetUser?.displayName ??
                  event.chatId ??
                  event.telegramAccountId ??
                  '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      )}

      {events.length === 0 && !loading && !error && (
        <div className="flex flex-col items-center gap-2 rounded-xl border border-border bg-card px-6 py-12 text-center">
          <p className="text-sm font-medium text-heading">Событий нет</p>
          <p className="max-w-[40ch] text-xs leading-relaxed text-muted-foreground">
            Под выбранный фильтр ничего не подошло. Отказы и ошибки появляются
            здесь только тогда, когда они действительно случались.
          </p>
        </div>
      )}

      {cursor && (
        <div className="mt-3 flex justify-center">
          <button
            type="button"
            className="rounded-full border border-brand bg-card px-5 py-2 text-sm font-medium text-brand transition-colors hover:bg-secondary disabled:opacity-45"
            disabled={loading}
            onClick={() => void load(false)}
          >
            {loading ? 'Загрузка…' : 'Показать ещё'}
          </button>
        </div>
      )}
    </div>
  );
}
